import { useIsPhone } from '../lib/use-media';
import { type ReactNode, useEffect, useId } from 'react';
import { useTheme } from '../lib/theme';
import { Icon } from './Icon';

function useEscape(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const { dz } = useTheme();
  const titleId = useId();
  useEscape(onClose);
  return (
    <div className={dz} style={{ background: 'transparent' }}>
      <div className="modalwrap">
        <button
          type="button"
          className="scrimbtn"
          aria-label="Fechar"
          tabIndex={-1}
          onClick={onClose}
        />
        <div
          className={wide ? 'modal wide' : 'modal'}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          style={{ position: 'relative' }}
        >
          <div className="modalhead">
            <h2 id={titleId}>{title}</h2>
            <button type="button" className="btn icon g" aria-label="Fechar" onClick={onClose}>
              <Icon name="close" />
            </button>
          </div>
          {children}
          {footer ? <div className="modalfoot">{footer}</div> : null}
        </div>
      </div>
    </div>
  );
}

export function Drawer({
  title,
  kicker,
  onClose,
  children,
}: {
  title: ReactNode;
  kicker?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const { dz } = useTheme();
  const titleId = useId();
  useEscape(onClose);
  return (
    <div className={dz} style={{ background: 'transparent' }}>
      <div className="drawerwrap">
        <button
          type="button"
          className="scrimbtn"
          aria-label="Fechar"
          tabIndex={-1}
          onClick={onClose}
        />
        <div
          className="drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          style={{ position: 'relative' }}
        >
          <div className="modalhead">
            <div>
              {kicker ? <p className="xs muted">{kicker}</p> : null}
              <h2 id={titleId}>{title}</h2>
            </div>
            <button type="button" className="btn icon g" aria-label="Fechar" onClick={onClose}>
              <Icon name="close" />
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

/** Bottom sheet for the phone layout. */
export function Sheet({
  label,
  onClose,
  children,
}: {
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEscape(onClose);
  return (
    <div className="sheetwrap">
      <button type="button" className="scrimbtn" aria-label="Fechar" onClick={onClose} />
      <div
        className="sheet col"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        style={{ gap: 12, position: 'relative' }}
      >
        <div className="grab" style={{ marginBottom: 0 }} />
        {children}
      </div>
    </div>
  );
}

/** Phone or desktop dialog. */
export function Panel({
  label,
  onClose,
  children,
}: {
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const phone = useIsPhone();
  return phone ? (
    <Sheet label={label} onClose={onClose}>
      <h2>{label}</h2>
      {children}
    </Sheet>
  ) : (
    <Modal title={label} onClose={onClose}>
      {children}
    </Modal>
  );
}
